import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MultiSelect from '../ui/MultiSelect';

const BulkUploadModal = ({ isOpen, onClose, onSubmit }) => {
  const { isAuthenticated, user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [selectedKitName, setSelectedKitName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const fileInputRef = useRef(null);
  const errorRef = useRef(null);
  const validationErrorRef = useRef(null);

  // Real data from API
  const [tenants, setTenants] = useState([]);
  const [kitNames, setKitNames] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [supportedQuestionTypes, setSupportedQuestionTypes] = useState({});
  const [questionTypesArray, setQuestionTypesArray] = useState([]);

  // Fetch data and reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm(); // Reset form state when modal opens
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch tenants, tags, and supported question types in parallel
      const [tenantsResponse, kitNamesResponse, tagsResponse, questionTypesResponse] = await Promise.all([
        superAdminAPI.getAllTenants({ size: 100 }), // Get first 100 tenants
        superAdminAPI.getKitNames(),
        superAdminAPI.getAllTags(),
        superAdminAPI.getSupportedQuestionTypes()
      ]);

      if (tenantsResponse.data?.content) {
        setTenants(tenantsResponse.data.content);
      }

      if (Array.isArray(kitNamesResponse.data)) {
        setKitNames(kitNamesResponse.data);
      }

      if (tagsResponse.data) {
        setAvailableTags(tagsResponse.data);
      }

      if (questionTypesResponse.data?.questionTypes) {
        setSupportedQuestionTypes(questionTypesResponse.data.questionTypes);

        // Convert to array format for dropdown
        const typesArray = Object.entries(questionTypesResponse.data.questionTypes).map(([key, value]) => ({
          value: key,
          label: value.label,
          description: value.description,
          requiredFields: value.requiredFields,
          optionalFields: value.optionalFields,
          fieldDescriptions: value.fieldDescriptions
        }));
        setQuestionTypesArray(typesArray);

        // Set initial question type if not already set
        if (!selectedQuestionType && typesArray.length > 0) {
          setSelectedQuestionType(typesArray[0].value);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty arrays on error so form still works
      setTenants([]);
      setKitNames([]);
      setAvailableTags([]);
      setSupportedQuestionTypes({});
      setQuestionTypesArray([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setValidationResult(null);
    setSelectedTenantIds([]);
    setSelectedKitName('');
    setSelectedTags([]);
    setNewTag('');
    setMessage('');
    setError('');
    setIsUploading(false);
    setIsValidating(false);
    setLoading(false);
    // Reset to first available question type
    if (questionTypesArray.length > 0) {
      setSelectedQuestionType(questionTypesArray[0].value);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        alert('Please select a CSV or Excel file (.csv, .xlsx, or .xls)');
        return;
      }

      setSelectedFile(file);
      setUploadResult(null);
      setValidationResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const handleAddNewTag = async () => {
    if (!newTag.trim()) return;

    try {
      // Clean the tag name
      const cleanTag = newTag.trim().toLowerCase().replace(/\s+/g, '_');

      // Check if tag already exists
      if (availableTags.includes(cleanTag)) {
        setError('Tag already exists');
        return;
      }

      // Add to selected tags
      setSelectedTags(prev => [...prev, cleanTag]);

      // Add to available tags for future use
      setAvailableTags(prev => [...prev, cleanTag].sort());

      // Clear the input
      setNewTag('');
      setError('');
    } catch (error) {
      console.error('Error adding new tag:', error);
      setError('Failed to add new tag');
    }
  };

  const downloadTemplate = async () => {
    try {
      setError('');
      setMessage('');

      console.log('Downloading template for question type:', selectedQuestionType);

      // Get template from API based on selected question type
      const response = await superAdminAPI.getExcelTemplate(selectedQuestionType);

      console.log('Template response received:', response);

      // Validate response
      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      console.log('Blob created, size:', blob.size);

      if (blob.size === 0) {
        throw new Error('Template file is empty');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename based on question type
      const selectedTypeConfig = questionTypesArray.find(type => type.value === selectedQuestionType);
      const typeName = selectedTypeConfig ? selectedTypeConfig.label.replace(/\s+/g, '_').toLowerCase() : 'questions';
      link.download = `question_template_${typeName}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('Excel template downloaded successfully!');
      console.log('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });

      let errorMessage = 'Failed to download template. ';
      if (error.response?.status === 404) {
        errorMessage += 'Template not found for this question type.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }

      setError(errorMessage);
    }
  };

  const validateFile = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setIsValidating(true);
    setError('');
    setMessage('');
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('questionType', selectedQuestionType);

      const response = await superAdminAPI.validateQuestionsFile(formData);
      setValidationResult(response.data);

      if (response.data.isValid) {
        if (response.data.warningCount > 0) {
          setMessage(`Validation successful with ${response.data.warningCount} warning(s): ${response.data.validRows} rows ready for upload`);
        } else {
          setMessage(`Validation successful: ${response.data.validRows} rows ready for upload`);
        }
      } else {
        setMessage(`Validation found ${response.data.errorCount} errors in ${response.data.totalRows} rows`);

        // Auto-scroll to validation errors after a short delay
        setTimeout(() => {
          if (validationErrorRef.current) {
            validationErrorRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            validationErrorRef.current.focus();
          }
        }, 100);
      }

    } catch (error) {
      console.error('Error validating file:', error);
      setError(error.response?.data?.message || 'Failed to validate file');

      // Auto-scroll to error message
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorRef.current.focus();
        }
      }, 100);
    } finally {
      setIsValidating(false);
    }
  };

  const downloadErrorReport = async () => {
    if (!selectedFile) return;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('questionType', selectedQuestionType);

      const response = await superAdminAPI.downloadQuestionsErrorReport(formData);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `validation_errors_${selectedFile.name.replace(/\.[^.]+$/, '')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading error report:', error);
      setError('Failed to download error report');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    // Check authentication first
    if (!isAuthenticated) {
      setError('Authentication required. Please log in with credentials: superadmin / admin123');
      return;
    }

    // If file hasn't been validated, show warning but allow upload
    if (!validationResult) {
      const confirmUpload = window.confirm(
        'File has not been validated. Do you want to upload without validation? ' +
        'This may result in errors if the file format is incorrect.'
      );
      if (!confirmUpload) {
        return;
      }
    } else if (!validationResult.isValid) {
      const confirmUpload = window.confirm(
        'File validation found errors. Do you want to proceed with upload anyway? ' +
        'Only valid rows will be imported.'
      );
      if (!confirmUpload) {
        return;
      }
    }

    setIsUploading(true);
    setError('');
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('validateOnly', 'false');
      formData.append('questionType', selectedQuestionType);

      // Add selected tenant IDs
      if (selectedTenantIds.length > 0) {
        selectedTenantIds.forEach(tenantId => {
          formData.append('tenantIds', tenantId);
        });
      }

      if (selectedKitName.trim()) {
        formData.append('kitName', selectedKitName.trim());
      }

      // Add selected tags
      if (selectedTags.length > 0) {
        selectedTags.forEach(tag => {
          formData.append('tags', tag);
        });
      }

      await onSubmit(formData);
      setMessage('Questions uploaded successfully!');
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500); // Show success message briefly before closing
    } catch (error) {
      console.error('Error uploading file:', error);

      // Handle specific error types
      if (error.message.includes('Authentication')) {
        setError('Authentication expired. Please refresh the page and log in again.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(error.message || 'Failed to upload questions. Please check your Excel format and try again.');
      }

      // Auto-scroll to error message
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          errorRef.current.focus();
        }
      }, 100);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const selectedTypeConfig = questionTypesArray.find(type => type.value === selectedQuestionType);
  const selectedTypeLabel = selectedTypeConfig?.label || 'Selected Question Type';
  const supportedTypeLabels = questionTypesArray.map(type => type.label).join(', ');

  const getCorrectAnswerGuidance = () => {
    switch (selectedQuestionType) {
      case 'MCQ_SINGLE_CORRECT':
        return 'Use a single option number in CORRECT_ANSWER (1, 2, 3, or 4).';
      case 'MCQ_MULTIPLE_CORRECT':
        return 'Use comma-separated option numbers in CORRECT_ANSWER (for example: 1,3).';
      case 'TRUE_FALSE':
        return 'Use TRUE or FALSE in CORRECT_ANSWER.';
      case 'FILL_IN_THE_BLANK_NUMERIC':
        return 'Use a numeric value in CORRECT_ANSWER (for example: 42 or 3.14).';
      default:
        return 'Follow the template for valid CORRECT_ANSWER format.';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white modal-responsive max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Bulk Upload Questions</h2>
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="text-slate-400 hover:text-slate-600 transition-colors duration-200 p-1"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 form-responsive">
          {/* Question Type Selection */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Question Type Selection</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Question Type
                </label>
                <select
                  value={selectedQuestionType}
                  onChange={(e) => {
                    setSelectedQuestionType(e.target.value);
                    // Reset file and validation when question type changes
                    setSelectedFile(null);
                    setValidationResult(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  {questionTypesArray.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Question Type Info */}
              {selectedQuestionType && questionTypesArray.length > 0 && (
                <div className="bg-white p-3 rounded border border-blue-200">
                  <div className="text-sm">
                    {(() => {
                      const selectedType = questionTypesArray.find(t => t.value === selectedQuestionType);
                      if (!selectedType) return null;

                      return (
                        <>
                          <p className="font-medium text-slate-900 mb-1">
                            {selectedType.label}
                          </p>
                          <p className="text-slate-600 mb-2">
                            {selectedType.description}
                          </p>
                          <div className="text-xs text-slate-500">
                            <p className="mb-1">
                              <strong>Required fields:</strong> {selectedType.requiredFields?.join(', ')}
                            </p>
                            {selectedType.optionalFields && selectedType.optionalFields.length > 0 && (
                              <p>
                                <strong>Optional fields:</strong> {selectedType.optionalFields.join(', ')}
                              </p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-slate-50 p-3 sm:p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Upload Instructions</h3>
            <ul className="text-xs sm:text-sm text-slate-600 space-y-1">
              <li>• Use `.csv`, `.xlsx`, or `.xls`, single sheet for Excel, max 10MB</li>
              <li>• Select question type and download the matching template</li>
              <li>• Required for <strong>{selectedTypeLabel}</strong>: {selectedTypeConfig?.requiredFields?.join(', ') || 'See template'}</li>
              <li>• {getCorrectAnswerGuidance()}</li>
              <li>• Set `DIFFICULTY_LEVEL` as EASY, MEDIUM, or HARD</li>
              <li>• `TENANT_CODE`: leave empty or use `CROSS_TENANT` for cross-tenant questions</li>
            </ul>
          </div>

          {/* Authentication Status */}
          {!isAuthenticated && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Authentication Required</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Please log in to validate and upload Excel files. Use: superadmin / admin123
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success/Error Messages */}
          {message && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-sm text-green-800">{message}</p>
              </div>
            </div>
          )}

          {error && (
            <div ref={errorRef} tabIndex={-1} className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Template Download */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Download Template</h4>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-700 mb-1">
                  Download a type-specific Excel template for <strong>{selectedTypeLabel}</strong>
                </p>
                <p className="text-xs text-slate-500 mb-1">
                  Required columns: {selectedTypeConfig?.requiredFields?.join(', ') || 'See template'}
                </p>
                <p className="text-xs text-slate-500">
                  Supported question types: {supportedTypeLabels || 'Loading...'}
                </p>
              </div>
              <button
                onClick={downloadTemplate}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center text-sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </button>
            </div>
          </div>

          {/* Upload Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Target Tenants (Optional)
              </label>
              <MultiSelect
                options={tenants.map(tenant => ({
                  value: tenant.id,
                  label: `${tenant.name} (${tenant.code || tenant.id})`
                }))}
                selected={selectedTenantIds}
                onChange={setSelectedTenantIds}
                placeholder="Select tenants..."
                loading={loading}
                emptyMessage="No tenants found"
                searchPlaceholder="Search tenants..."
              />
              <p className="text-xs text-slate-500 mt-1">
                Questions will be assigned to selected tenants. If empty, questions are available for all tenants (cross-tenant)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Kit Name (Optional)
              </label>
              <input
                type="text"
                list="bulk-upload-kit-suggestions"
                value={selectedKitName}
                onChange={(e) => setSelectedKitName(e.target.value)}
                placeholder="Type or select a kit name..."
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              <datalist id="bulk-upload-kit-suggestions">
                {kitNames.map(kitName => (
                  <option key={kitName} value={kitName} />
                ))}
              </datalist>
              <p className="text-xs text-slate-500 mt-1">
                Existing kit tags will be applied automatically. If the kit is new, legacy kit tags will still be attached for test creation.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Tags (Optional)
            </label>
            <MultiSelect
              options={availableTags.map(tag => ({
                value: tag,
                label: tag
              }))}
              selected={selectedTags}
              onChange={setSelectedTags}
              placeholder="Select tags..."
              loading={loading}
              emptyMessage="No tags found"
              searchPlaceholder="Search tags..."
            />
            <p className="text-xs text-slate-500 mt-1">
              Questions will be tagged with selected tags. If empty, will auto-generate tags with timestamp
            </p>

            {/* Add new tag functionality */}
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Add New Tag
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter new tag name..."
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddNewTag}
                  disabled={!newTag.trim()}
                  className="px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-cyan-400 transition-colors duration-200"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                Drop your file here or click to browse
              </p>
              <p className="text-xs text-slate-500">
                Supports CSV/Excel files (.csv, .xlsx, .xls) for {questionTypesArray.find(t => t.value === selectedQuestionType)?.label || 'selected question type'}, single sheet only for Excel, up to 10MB
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-primary mt-4"
            >
              Select File
            </button>
          </div>

          {/* Selected File */}
          {selectedFile && (
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-6 w-6 text-cyan-600" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Enhanced Validation Results */}
          {validationResult && (
            <div className="border border-slate-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Validation Results</h4>
                <div className={`flex items-center space-x-2 ${validationResult.isValid ? 'text-green-600' : 'text-red-600'}`}>
                  {validationResult.isValid ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <AlertCircle className="h-5 w-5" />
                  )}
                  <span className="font-medium">
                    {validationResult.isValid ? 'Valid' : 'Has Errors'}
                  </span>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">{validationResult.totalRows}</div>
                  <div className="text-xs text-blue-700">Total Rows</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{validationResult.validRows}</div>
                  <div className="text-xs text-green-700">Valid Rows</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-lg font-bold text-yellow-600">{validationResult.warningCount || 0}</div>
                  <div className="text-xs text-yellow-700">Warnings</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-lg font-bold text-red-600">{validationResult.errorCount}</div>
                  <div className="text-xs text-red-700">Errors</div>
                </div>
              </div>

              {/* Warning Details */}
              {validationResult.warnings && validationResult.warnings.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-yellow-800">Validation Warnings</h5>
                    <button
                      type="button"
                      onClick={downloadErrorReport}
                      className="flex items-center space-x-2 text-sm text-yellow-600 hover:text-yellow-800"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download Report</span>
                    </button>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div className="text-sm text-yellow-700 space-y-1">
                      {validationResult.warnings.slice(0, 5).map((warning, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          {typeof warning === 'string' ? (
                            <span className="text-yellow-700">{warning}</span>
                          ) : (
                            <>
                              <span className="font-medium">Row {warning.rowNumber}:</span>
                              <span>[{warning.warningType}] {warning.message}</span>
                            </>
                          )}
                        </div>
                      ))}
                      {validationResult.warnings.length > 5 && (
                        <div className="text-xs text-yellow-600 italic">
                          ... and {validationResult.warnings.length - 5} more warnings. Download full report for details.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Details */}
              {validationResult.errors && validationResult.errors.length > 0 && (
                <div ref={validationErrorRef} tabIndex={-1} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-red-800">Validation Errors</h5>
                    <button
                      type="button"
                      onClick={downloadErrorReport}
                      className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-800"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download Error Report</span>
                    </button>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div className="text-sm text-red-700 space-y-1">
                      {validationResult.errors.slice(0, 10).map((error, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          {typeof error === 'string' ? (
                            <span>{error}</span>
                          ) : (
                            <>
                              <span className="font-medium">Row {error.rowNumber}:</span>
                              <span>[{error.errorType}] {error.message}</span>
                            </>
                          )}
                        </div>
                      ))}
                      {validationResult.errors.length > 10 && (
                        <div className="text-xs text-red-600 italic">
                          ... and {validationResult.errors.length - 10} more errors. Download full report for details.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {validationResult.isValid && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">File is ready for upload!</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    All {validationResult.validRows} rows passed validation and can be uploaded.
                    {validationResult.warningCount > 0 && (
                      <span className="text-yellow-600">
                        {' '}Note: {validationResult.warningCount} warning(s) found but upload can proceed.
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={() => { resetForm(); onClose(); }}
              className="btn-secondary"
              disabled={isUploading || isValidating}
            >
              Cancel
            </button>

            {selectedFile && (
              <button
                type="button"
                onClick={validateFile}
                className="btn-secondary"
                disabled={isUploading || isValidating || !isAuthenticated}
              >
                {isValidating ? 'Validating...' : 'Validate File'}
              </button>
            )}

            <button
              type="button"
              onClick={handleUpload}
              className="btn-primary"
              disabled={!selectedFile || isUploading || isValidating || !isAuthenticated}
            >
              {isUploading ? 'Uploading...' : 'Upload Questions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
